"use strict";
/**
 * @license
 * Copyright 2023 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { composeByteRangeRequest } from "#src/kvstore/byte_range/file_handle.js";
import { KvStoreFileHandle, NotFoundError } from "#src/kvstore/index.js";
import { fetchOk, HttpError, isNotFoundError } from "#src/util/http_request.js";
function getRangeHeader(request) {
  if (request === void 0) return void 0;
  return `bytes=${request.offset}-${request.offset + request.length - 1}`;
}
const byteRangeCacheMode = navigator.userAgent.indexOf("Chrome") !== -1 ? "no-store" : "default";
function wasRedirectedToDirectoryListing(url, response) {
  return new URL(url).pathname + "/" === new URL(response.url).pathname;
}
function parse206ContentRangeHeader(contentRange) {
  const m = contentRange.match(/bytes ([0-9]+)-([0-9]+)\/([0-9]+|\*)/);
  if (m === null) {
    throw new Error(
      `Invalid content-range header: ${JSON.stringify(contentRange)}`
    );
  }
  const offset = parseInt(m[1], 10);
  const endPos = parseInt(m[2], 10);
  let totalSize;
  if (m[3] !== "*") {
    totalSize = parseInt(m[3], 10);
  }
  const length = endPos - offset + 1;
  return { offset, length, totalSize };
}
export async function read(store, key, url, options, fetchOkImpl = fetchOk) {
  let resolvedByteRange;
  try {
    const { byteRange: byteRangeRequest } = options;
    let rangeHeader;
    if (byteRangeRequest !== void 0) {
      if ("suffixLength" in byteRangeRequest) {
        const statResponse = await stat(store, key, url, options, fetchOkImpl);
        if (statResponse === void 0) return void 0;
        const { totalSize: totalSize2 } = statResponse;
        if (totalSize2 === void 0) {
          throw new Error(
            `Failed to determine total size of ${store.getUrl(key)} in order to fetch suffix ${JSON.stringify(byteRangeRequest)}`
          );
        }
        resolvedByteRange = composeByteRangeRequest(
          { offset: 0, length: totalSize2 },
          byteRangeRequest
        ).outer;
        if (resolvedByteRange.length === 0) {
          return {
            ...resolvedByteRange,
            totalSize: totalSize2,
            response: new Response(new Uint8Array(0))
          };
        }
        rangeHeader = getRangeHeader(resolvedByteRange);
      } else {
        resolvedByteRange = byteRangeRequest;
        if (resolvedByteRange.length === 0) {
          rangeHeader = getRangeHeader({
            offset: Math.max(resolvedByteRange.offset - 1, 0),
            length: 1
          });
        } else {
          rangeHeader = getRangeHeader(resolvedByteRange);
        }
      }
    }
    const requestInit = {
      signal: options.signal,
      progressListener: options.progressListener
    };
    if (rangeHeader !== void 0) {
      requestInit.headers = { range: rangeHeader };
      requestInit.cache = byteRangeCacheMode;
    }
    let response = await fetchOkImpl(url, requestInit);
    if (wasRedirectedToDirectoryListing(url, response)) {
      return void 0;
    }
    let offset;
    let length;
    let totalSize;
    if (response.status === 206) {
      const contentRange = response.headers.get("content-range");
      if (contentRange === null) {
        if (resolvedByteRange !== void 0) {
          offset = resolvedByteRange.offset;
        } else {
          throw new Error(
            "Unexpected HTTP 206 response when no byte range specified."
          );
        }
      }
      if (contentRange !== null) {
        ({ offset, length, totalSize } = parse206ContentRangeHeader(contentRange));
      }
    } else {
      length = totalSize = getBodyLength(response.headers);
    }
    if (offset === void 0) {
      offset = 0;
    }
    if (length === void 0) {
      length = getBodyLength(response.headers);
    }
    if (resolvedByteRange?.length === 0) {
      response = new Response(new Uint8Array(0));
      offset = resolvedByteRange.offset;
      length = 0;
    }
    return {
      response,
      offset,
      length,
      totalSize
    };
  } catch (e) {
    if (e instanceof HttpError && e.status === 416 && resolvedByteRange?.length === 0 && resolvedByteRange.offset === 0) {
      return {
        response: new Response(new Uint8Array(0)),
        offset: 0,
        length: 0,
        totalSize: 0
      };
    }
    return handleThrowIfMissing(store, key, options, e);
  }
}
function getBodyLength(headers) {
  const contentLength = headers.get("content-length");
  const contentEncoding = headers.get("content-encoding");
  if (contentEncoding === null && contentLength !== null) {
    const size = Number(contentLength);
    if (!Number.isFinite(size) || size < 0) {
      throw new Error(`Invalid content-length: {contentLength}`);
    }
    return size;
  }
  return void 0;
}
function handleThrowIfMissing(store, key, options, error) {
  if (isNotFoundError(error)) {
    if (options.throwIfMissing === true) {
      throw new NotFoundError(new KvStoreFileHandle(store, key), {
        cause: error
      });
    }
    return void 0;
  }
  throw error;
}
export async function stat(store, key, url, options, fetchOkImpl = fetchOk) {
  try {
    const response = await fetchOkImpl(url, {
      method: "HEAD",
      signal: options.signal,
      progressListener: options.progressListener
    });
    if (wasRedirectedToDirectoryListing(url, response)) return void 0;
    return { totalSize: getBodyLength(response.headers) };
  } catch (e) {
    if (e instanceof HttpError && (e.status === 405 || e.status === 501)) {
    } else {
      return handleThrowIfMissing(store, key, options, e);
    }
  }
  try {
    const response = await fetchOkImpl(url, {
      signal: options.signal,
      progressListener: options.progressListener,
      headers: { range: "bytes=0-0" }
    });
    if (wasRedirectedToDirectoryListing(url, response)) return void 0;
    let totalSize;
    if (response.status === 200) {
      totalSize = getBodyLength(response.headers);
    } else {
      const contentRange = response.headers.get("content-range");
      if (contentRange !== null) {
        ({ totalSize } = parse206ContentRangeHeader(contentRange));
      }
    }
    return { totalSize };
  } catch (e) {
    if (e instanceof HttpError && e.status === 416) {
      return { totalSize: 0 };
    }
    return handleThrowIfMissing(store, key, options, e);
  }
}
